import { CalendarClock, FileText, Mail, MessageCircle, MessageSquareText, Zap, } from "lucide-react";
import { Section, SectionHeading, PageHero } from "../components/site/section";
import { PageCta } from "../components/site/page-cta";
import { cn } from "../lib/utils";
import { asset } from "../lib/assets";

const emailAlertAsset = { url: asset("emailalert.png") };
const smsAlertAsset = { url: asset("smsalert.png") };

const emailFeatures = [
    {
        icon: Zap,
        title: "Immediate or Digest Delivery",
        body: "Get alerts when Violerts detects new or updated records, or roll them into a scheduled digest that fits your workflow.",
    },
    {
        icon: CalendarClock,
        title: "Property & Portfolio Routing",
        body: "Route different buildings, portfolios, or boroughs to the right person or shared inbox.",
    },
    {
        icon: FileText,
        title: "Rich Report Attachments",
        body: "Each email can include the full violation record, deadlines, and relevant property context.",
    },
    {
        icon: MessageCircle,
        title: "Per-User Preferences",
        body: "Team members choose their own alert classes, quiet hours, and digest windows.",
    },
];
const smsFeatures = [
    {
        icon: Zap,
        title: "Instant Urgent Alerts",
        body: "Real-time SMS for Stop Work Orders, Emergency Vacates, and active summonses when Violerts detects them.",
    },
    {
        icon: CalendarClock,
        title: "Hearing & Deadline Reminders",
        body: "OATH & ECB hearing dates and critical DOB certificate deadlines sent directly to your phone.",
    },
    {
        icon: FileText,
        title: "Daily Smart Digest",
        body: "Consolidated daily SMS summary of all standard property updates and filings.",
    },
    {
        icon: MessageCircle,
        title: "Reply to Resolve via SMS",
        body: "Reply directly from your phone to resolve violations.",
    },
];
function Alerts() {
    return (<>
      <PageHero title="Know within hours, not when the penalty letter arrives." description="Every new filing, penalty and status change, pushed to you when Violerts detects a new or updated record."/>
      <Section className="bg-secondary/50">
        <SectionHeading title="What lands in your inbox"/>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Timing depends on when an agency publishes the record, when Violerts processes it, and delivery by your email or mobile provider.
        </p>
        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-foreground/10 text-foreground">
                <Zap className="h-5 w-5" aria-hidden="true"/>
              </span>
              <div>
                <h2 className="text-2xl font-bold">Stay Alerted</h2>
                <p className="mt-1 text-sm font-medium text-foreground">Free with every account</p>
              </div>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Alerts fire for anything <strong className="text-foreground">new</strong> that happens on a property — a fresh violation, complaint, permit, or inspection. Included at no cost whenever you have a Violerts account.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-foreground/10 text-foreground">
                <MessageCircle className="h-5 w-5" aria-hidden="true"/>
              </span>
              <div>
                <h2 className="text-2xl font-bold">Stay Notified</h2>
                <p className="mt-1 text-sm font-medium text-foreground">Requires a paid plan</p>
              </div>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              Notifications fire for <strong className="text-foreground">any change</strong> to an existing item on a property — status updates, deadline changes, hearing reschedules, or amended records. Requires a paid plan.
            </p>
          </div>
        </div>
      </Section>
      <Section>
        <SectionHeading title="How you get them"/>
        <div className="mt-12 flex flex-col gap-8">
          <DeliveryCard icon={Mail} title="In your inbox" description="Digest or immediate, per person or per shared inbox." badge="Available in all plans" features={emailFeatures} note="Email delivery is included with every Violerts plan at no extra cost." image={emailAlertAsset.url} imageAlt="Example Violerts email alert" imageSide="right"/>
          <DeliveryCard icon={MessageSquareText} title="SMS Alerts Add On" description="Supercharge any plan with instantaneous SMS text alerts for critical building events." badge="Requires an upgrade" features={smsFeatures} note="Add to any plan during checkout or enable directly from your dashboard settings." image={smsAlertAsset.url} imageAlt="Example Violerts SMS alert" imageSide="left"/>
        </div>
      </Section>
      <PageCta />
    </>);
}
function DeliveryCard({ icon: Icon, title, description, badge, features, note, image, imageAlt, imageSide = "right", }) {
    const left = imageSide === "left";
    return (<article className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
      <div className="grid gap-8 md:grid-cols-2 md:items-center">
        <div className={cn(left ? "md:order-2" : "md:order-1")}>
          <div className="flex flex-wrap items-start justify-between gap-6 md:flex-nowrap">
            <div className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-foreground/10 text-foreground">
                <Icon className="h-5 w-5" aria-hidden="true"/>
              </span>
              <div>
                <h2 className="text-2xl font-bold">{title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{description}</p>
              </div>
            </div>
            {badge && (<span className="shrink-0 whitespace-nowrap rounded-full bg-accent/15 px-3 py-1 text-sm font-bold text-foreground">
                {badge}
              </span>)}
          </div>

          <div className="my-7 border-t border-border"/>

          <ul className="space-y-5">
            {features.map((feature) => (<li key={feature.title} className="flex gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-foreground/10 text-foreground">
                  <feature.icon className="h-4 w-4" aria-hidden="true"/>
                </span>
                <div>
                  <h3 className="text-sm font-bold">{feature.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{feature.body}</p>
                </div>
              </li>))}
          </ul>

          <div className="my-7 border-t border-border"/>
          <p className="text-sm text-muted-foreground">{note}</p>
        </div>

        {image && (<div className={cn("md:self-center", left ? "md:order-1" : "md:order-2")}>
            <div className="overflow-hidden rounded-xl border border-border bg-black/5">
              <img src={image} alt={imageAlt || `${title} preview`} className="h-auto w-full object-contain"/>
            </div>
          </div>)}
      </div>
    </article>);
}

export default Alerts;
